export class ApiError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly response: string;
  /** From the response's `Retry-After` header, when the server sent one. */
  public readonly retryAfterMs?: number;

  constructor(
    status: number,
    statusText: string,
    response: string = '',
    retryAfterMs?: number
  ) {
    super(
      `Api error ${status}: ${statusText}${response ? ` - ${response}` : ''}`
    );
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.response = response;
    this.retryAfterMs = retryAfterMs;
  }

  get serverMessage(): string | undefined {
    if (!this.response) {
      return undefined;
    }
    try {
      const body = JSON.parse(this.response);
      return body?.message || undefined;
    } catch {
      return undefined;
    }
  }

  get serverPayload(): Record<string, unknown> | undefined {
    if (!this.response) {
      return undefined;
    }
    try {
      const body = JSON.parse(this.response);
      return body && typeof body === 'object' ? body : undefined;
    } catch {
      return undefined;
    }
  }
}

/**
 * `Retry-After` is either delay-seconds or an HTTP-date. Returns
 * milliseconds from `now`, or undefined when absent or unparseable.
 */
export function parseRetryAfterMs(
  header: string | null | undefined,
  now: number = Date.now()
): number | undefined {
  const value = header?.trim();
  if (!value) {
    return undefined;
  }
  if (/^\d+$/.test(value)) {
    return Number(value) * 1000;
  }
  // An HTTP-date always names its month; this keeps Date.parse from
  // reading something like "-5" as a year.
  const at = /[a-z]/i.test(value) ? Date.parse(value) : NaN;
  return Number.isNaN(at) ? undefined : Math.max(0, at - now);
}

export function extractApiErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (error instanceof ApiError) {
    return error.serverMessage || fallback;
  }
  return fallback;
}

/**
 * Like `extractApiErrorMessage`, but keeps the message of a plain `Error`.
 *
 * Use this where the same catch block sees both transport failures and errors
 * the caller threw itself after inspecting a response — throwing away the
 * latter's message would replace a specific reason with a generic fallback.
 * Only `ApiError.message` needs suppressing, because it embeds the raw response
 * body ("Api error 403: Forbidden - {json}").
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && !(error instanceof ApiError)) {
    return error.message || fallback;
  }
  return extractApiErrorMessage(error, fallback);
}
